<template>
  <div class="container">
    <div class="container01">
      <h1 class="container01_h1">数据统计</h1>
      
      <!-- 添加返回按钮 -->
      <div class="header-actions">
        <el-button @click="$router.back()" class="back-button">
          <i class="el-icon-arrow-left"></i> 返回上一级
        </el-button>
      </div>
      
      <el-tabs v-model="activeTab" type="card" class="stats-tabs">
        <!-- 每日采购汇总 -->
        <el-tab-pane label="每日采购汇总" name="daily">
          <el-card class="stats-card">
            <el-form :model="dailyForm" label-width="100px" class="filter-form">
              <el-form-item label="统计日期">
                <el-date-picker
                  v-model="dailyForm.date"
                  type="date"
                  placeholder="选择统计日期"
                  value-format="YYYY-MM-DD"
                  @change="loadDailySummary">
                </el-date-picker>
              </el-form-item>
            </el-form>
            
            <!-- 添加表格容器，设置固定高度 -->
            <div class="table-container">
              <el-table 
                :data="dailyData.categories" 
                v-loading="dailyLoading" 
                border
                style="width: 100%"
                :height="tableHeight">
                <el-table-column type="expand">
                  <template #default="props">
                    <div class="nested-table-container">
                      <el-table :data="props.row.merchandises" border>
                        <el-table-column prop="name" label="商品名称" width="200"></el-table-column>
                        <el-table-column prop="specification" label="规格" width="150"></el-table-column>
                        <el-table-column prop="total_quantity" label="总数量" width="100" align="right">
                          <template #default="scope">
                            {{ scope.row.total_quantity }}
                          </template>
                        </el-table-column>
                        <el-table-column prop="customer_demands" label="客户需求来源"></el-table-column>
                      </el-table>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="category" label="商品分类"></el-table-column>
                <el-table-column label="商品数量" width="120" align="right">
                  <template #default="props">
                    {{ props.row.merchandises?.length || 0 }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <div class="summary-info">
              <p>统计日期: {{ dailyData.date }} | 分类数量: {{ dailyData.total_categories }} | 商品种类: {{ dailyData.total_merchandises }}</p>
            </div>
          </el-card>
        </el-tab-pane>
        
        <!-- 月度客户汇总 -->
        <el-tab-pane label="月度客户汇总" name="monthly">
          <el-card class="stats-card">
            <el-form :model="monthlyForm" label-width="100px" class="filter-form">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="年份">
                    <el-input-number
                      v-model="monthlyForm.year"
                      :min="2020"
                      :max="2030"
                      @change="loadMonthlySummary">
                    </el-input-number>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="月份">
                    <el-input-number
                      v-model="monthlyForm.month"
                      :min="1"
                      :max="12"
                      @change="loadMonthlySummary">
                    </el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
            
            <!-- 添加表格容器，设置固定高度 -->
            <div class="table-container">
              <el-table 
                :data="monthlyData.customers" 
                v-loading="monthlyLoading" 
                border
                style="width: 100%"
                :height="tableHeight">
                <el-table-column type="expand">
                  <template #default="props">
                    <div class="nested-table-container">
                      <el-table :data="props.row.orders" border>
                        <el-table-column prop="order_number" label="订单编号" width="150"></el-table-column>
                        <el-table-column prop="order_time" label="下单时间" width="180">
                          <template #default="scope">
                            {{ formatDateTime(scope.row.order_time) }}
                          </template>
                        </el-table-column>
                        <el-table-column prop="amount" label="订单金额" width="120" align="right">
                          <template #default="scope">
                            ¥{{ scope.row.amount?.toFixed(2) || '0.00' }}
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="客户名称"></el-table-column>
                <el-table-column prop="total_amount" label="总金额" width="150" align="right">
                  <template #default="scope">
                    <span class="amount-highlight">
                      ¥{{ scope.row.total_amount?.toFixed(2) || '0.00' }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="订单数量" width="120" align="right">
                  <template #default="props">
                    {{ props.row.orders?.length || 0 }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <div class="summary-info">
              <p>
                {{ monthlyData.year }}年{{ monthlyData.month }}月 | 
                客户数量: {{ monthlyData.total_customers }} | 
                总金额: ¥{{ monthlyData.total_amount?.toFixed(2) || '0.00' }}
              </p>
            </div>
          </el-card>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import statisticsAPI from '@/api/statistics'

export default {
  name: 'StatisticsView',
  data() {
    const today = new Date()
    return {
      activeTab: 'daily',
      dailyLoading: false,
      monthlyLoading: false,
      dailyForm: {
        date: today.toISOString().split('T')[0]
      },
      monthlyForm: {
        year: today.getFullYear(),
        month: today.getMonth() + 1
      },
      dailyData: {
        date: '',
        total_categories: 0,
        total_merchandises: 0,
        categories: []
      },
      monthlyData: {
        year: 0,
        month: 0,
        total_customers: 0,
        total_amount: 0,
        customers: []
      },
      tableHeight: '500px'
    }
  },
  created() {
    this.loadDailySummary()
    this.loadMonthlySummary()
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      this.tableHeight = `${windowHeight - 400}px`
    },
    async loadDailySummary() {
      this.dailyLoading = true
      try {
        const response = await statisticsAPI.getDailyPurchaseSummary(this.dailyForm.date)
        this.dailyData = response
      } catch (error) {
        console.error('加载每日汇总失败:', error)
        ElMessage.error('加载每日汇总失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.dailyLoading = false
      }
    },
    async loadMonthlySummary() {
      this.monthlyLoading = true
      try {
        const response = await statisticsAPI.getMonthlyCustomerSummary(
          this.monthlyForm.year, 
          this.monthlyForm.month
        )
        this.monthlyData = response
      } catch (error) {
        console.error('加载月度汇总失败:', error)
        ElMessage.error('加载月度汇总失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.monthlyLoading = false
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  animation: fadeIn 0.3s ease;
}
.container01_h1 {
  text-align: center;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #1e293b, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.header-actions {
  margin-bottom: 20px;
}
.back-button {
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 40px;
  transition: all 0.2s;
}
.back-button:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
}
.stats-tabs {
  margin-top: 20px;
}
.stats-card {
  margin-bottom: 20px;
  transition: var(--transition);
}
.filter-form {
  margin-bottom: 24px;
  background: #f8fafc;
  padding: 16px;
  border-radius: 20px;
}
.table-container {
  position: relative;
  min-height: 400px;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  box-shadow: var(--card-shadow);
}
.nested-table-container {
  padding: 16px;
  background-color: #fefce8;
  border-radius: 12px;
  margin: 4px 0;
}
.amount-highlight {
  font-weight: bold;
  color: var(--danger-color);
  text-shadow: 0 0 1px rgba(239,68,68,0.3);
}
.summary-info {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff, #ffffff);
  border-radius: 16px;
  text-align: center;
  font-size: 14px;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.el-table__row:hover > td {
  background-color: #fef9e3 !important;
}
@media (max-width: 768px) {
  .container {
    padding: 12px !important;
  }
  .filter-form .el-col {
    width: 100% !important;
    margin-bottom: 12px;
  }
  .el-form-item {
    margin-bottom: 12px;
  }
  .el-input-number {
    width: 100%;
  }
  .table-container {
    overflow-x: auto;
  }
  .el-table {
    font-size: 12px;
  }
  .nested-table-container {
    padding: 8px;
  }
  .summary-info {
    font-size: 12px;
    padding: 12px;
  }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>